import { z } from 'zod';

import { isDestinationReelUrl } from '../utils/destination-reel-url.js';

import { WebsitePublishStatusSchema } from './admin-website-pages.js';
import { UuidSchema, IsoDateTimeSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

export { WebsitePublishStatusSchema };

export const DestinationPageSliderInputSchema = z.object({
  marketingTagId: UuidSchema,
  heading: z.string().min(1).max(120),
  subheading: z.string().max(200).nullish(),
});
export const DestinationPageFaqInputSchema = z.object({
  question: z.string().min(1).max(300),
  answerHtml: z.string().min(1).max(20000),
});
export const DestinationPageReelInputSchema = z.object({
  url: z.string().trim().refine(isDestinationReelUrl, {
    message:
      'Must be a YouTube Shorts URL (youtube.com/shorts/…) or Instagram Reel URL (instagram.com/reel/…)',
  }),
  posterMediaId: UuidSchema.nullish(),
});
export const DestinationPageSeoLinkInputSchema = z.object({
  groupLabel: z.string().min(1).max(120),
  label: z.string().min(1).max(120),
  href: z.string().min(1).max(300),
});
export const DestinationPageReviewInputSchema = z.object({
  customerName: z.string().min(1).max(120),
  summary: z.string().min(1).max(2000),
  rating: z.number().int().min(1).max(5).default(5),
  portraitMediaId: UuidSchema.nullish(),
  imageMediaIds: z.array(UuidSchema).max(5).default([]),
});

const CoreFields = z.object({
  slug: z.string().min(1).max(160),
  destinationId: UuidSchema.nullish(),
  title: z.string().min(1).max(200),
  heroHeading: z.string().max(200).nullish(),
  heroSubheading: z.string().max(300).nullish(),
  heroMediaId: UuidSchema.nullish(),
  heroVideoId: UuidSchema.nullish(),
  /** Image used for this destination's tile on a category page (falls back to hero). */
  tileMediaId: UuidSchema.nullish(),
  /** Heading for the About section (defaults to "About {title}" when empty). */
  aboutHeading: z.string().max(200).nullish(),
  // No length cap - "Read More" article copy can run long (5,000+ words).
  aboutHtml: z.string().nullish(),
  metaTitle: z.string().max(200).nullish(),
  metaDescription: z.string().max(400).nullish(),
  metaKeywords: z.string().max(400).nullish(),
  ogImageId: UuidSchema.nullish(),
  status: WebsitePublishStatusSchema.default('DRAFT'),
  sortOrder: z.number().int().min(0).default(0),
});

const childArrays = {
  categoryPageIds: z.array(UuidSchema).max(20).default([]),
  sliders: z.array(DestinationPageSliderInputSchema).max(30).default([]),
  faqs: z.array(DestinationPageFaqInputSchema).max(50).default([]),
  reels: z.array(DestinationPageReelInputSchema).max(30).default([]),
  seoLinks: z.array(DestinationPageSeoLinkInputSchema).max(200).default([]),
  reviews: z.array(DestinationPageReviewInputSchema).max(50).default([]),
};

export const CreateWebsiteDestinationPageRequestSchema = CoreFields.extend(childArrays);
export type CreateWebsiteDestinationPageRequest = z.infer<
  typeof CreateWebsiteDestinationPageRequestSchema
>;

// Partial parent fields; child arrays optional (when present, they bulk-set).
export const UpdateWebsiteDestinationPageRequestSchema = CoreFields.partial().extend({
  categoryPageIds: z.array(UuidSchema).max(20).optional(),
  sliders: z.array(DestinationPageSliderInputSchema).max(30).optional(),
  faqs: z.array(DestinationPageFaqInputSchema).max(50).optional(),
  reels: z.array(DestinationPageReelInputSchema).max(30).optional(),
  seoLinks: z.array(DestinationPageSeoLinkInputSchema).max(200).optional(),
  reviews: z.array(DestinationPageReviewInputSchema).max(50).optional(),
});
export type UpdateWebsiteDestinationPageRequest = z.infer<
  typeof UpdateWebsiteDestinationPageRequestSchema
>;

export const WebsiteDestinationPageListQuerySchema = PaginationQuerySchema.extend({
  status: WebsitePublishStatusSchema.optional(),
  /**
   * Only pages missing information the public site needs. Same predicate the
   * dashboard's Needs-attention card counts with, so its chips can deep-link
   * here and land on exactly the rows they promised.
   */
  incomplete: BooleanQueryParamSchema.optional(),
  /**
   * Narrows `incomplete` to one gap bucket: `SEO` = meta title / description /
   * OG image missing; `CONTENT` = hero / about / FAQs / sliders missing. The
   * dashboard's "missing SEO" / "missing content" chips deep-link here.
   */
  missing: z.enum(['SEO', 'CONTENT']).optional(),
  /**
   * Deep-link targets for the dashboard's breakdown tables. `none` = not
   * linked to any live destination master, the same rule the breakdown counts
   * with.
   */
  destinationId: z.union([UuidSchema, z.literal('none')]).optional(),
  /** Whether the linked destination master is international or domestic. */
  destinationScope: z.enum(['international', 'domestic', 'none']).optional(),
});
export type WebsiteDestinationPageListQuery = z.infer<typeof WebsiteDestinationPageListQuerySchema>;

export const WebsiteDestinationPageListItemSchema = z.object({
  id: UuidSchema,
  slug: z.string(),
  title: z.string(),
  destinationId: UuidSchema.nullable(),
  primaryCategorySlug: z.string().nullable(),
  status: WebsitePublishStatusSchema,
  publishedAt: IsoDateTimeSchema.nullable(),
  sortOrder: z.number().int(),
  updatedAt: IsoDateTimeSchema,
});
export type WebsiteDestinationPageListItem = z.infer<typeof WebsiteDestinationPageListItemSchema>;

export const WebsiteDestinationPageListResponseSchema = paginatedResponseSchema(
  WebsiteDestinationPageListItemSchema,
);
export type WebsiteDestinationPageListResponse = z.infer<
  typeof WebsiteDestinationPageListResponseSchema
>;

// Detail returns children with their stored ids (raw media ids, not URLs).
const sliderOut = DestinationPageSliderInputSchema.extend({
  id: UuidSchema,
  sortOrder: z.number().int(),
});
const faqOut = DestinationPageFaqInputSchema.extend({
  id: UuidSchema,
  sortOrder: z.number().int(),
});
const reelOut = DestinationPageReelInputSchema.extend({
  id: UuidSchema,
  sortOrder: z.number().int(),
});
const seoLinkOut = DestinationPageSeoLinkInputSchema.extend({
  id: UuidSchema,
  sortOrder: z.number().int(),
});
const reviewOut = DestinationPageReviewInputSchema.extend({
  id: UuidSchema,
  sortOrder: z.number().int(),
});

/** Collections that have at least one active package for a destination (slider suggestions). */
export const DestinationPageCollectionCandidateSchema = z.object({
  marketingTagId: UuidSchema,
  name: z.string(),
  description: z.string().nullable(),
  packageCount: z.number().int().min(1),
});
export type DestinationPageCollectionCandidate = z.infer<
  typeof DestinationPageCollectionCandidateSchema
>;

export const DestinationPageCollectionCandidatesQuerySchema = z.object({
  destinationId: UuidSchema,
});
export type DestinationPageCollectionCandidatesQuery = z.infer<
  typeof DestinationPageCollectionCandidatesQuerySchema
>;

export const DestinationPageCollectionCandidatesResponseSchema = z.array(
  DestinationPageCollectionCandidateSchema,
);
export type DestinationPageCollectionCandidatesResponse = z.infer<
  typeof DestinationPageCollectionCandidatesResponseSchema
>;

/**
 * Live check the Reels editor runs while an editor types a reel link, so a dead
 * or unsupported URL is caught before save rather than silently rendering an
 * empty card on the website.
 *
 * `no_cover` is deliberately separate from `not_found`: Instagram blocks
 * server-side scraping often enough that "we could not get a cover" must not be
 * reported as "this reel does not exist". It asks for a poster upload instead.
 */
export const DestinationReelPreviewStatusSchema = z.enum([
  /** Reel resolved and we have a cover image for it. */
  'ok',
  /** Link is valid and supported, but no cover came back - upload a poster. */
  'no_cover',
  /** Provider says there is no such reel (deleted, private, or wrong id). */
  'not_found',
  /** Not a YouTube Shorts or Instagram Reel URL at all. */
  'invalid',
]);
export type DestinationReelPreviewStatus = z.infer<typeof DestinationReelPreviewStatusSchema>;

export const DestinationReelPreviewQuerySchema = z.object({
  url: z.string().trim().min(1).max(500),
});
export type DestinationReelPreviewQuery = z.infer<typeof DestinationReelPreviewQuerySchema>;

export const DestinationReelPreviewSchema = z.object({
  status: DestinationReelPreviewStatusSchema,
  provider: z.enum(['youtube', 'instagram']).nullable(),
  thumbnailUrl: z.string().nullable(),
  /** Video title, when the provider hands one over (YouTube oEmbed). */
  title: z.string().nullable(),
  /** Editor-facing explanation for every non-`ok` status. */
  message: z.string().nullable(),
});
export type DestinationReelPreview = z.infer<typeof DestinationReelPreviewSchema>;

export const WebsiteDestinationPageDetailSchema = z.object({
  id: UuidSchema,
  slug: z.string(),
  destinationId: UuidSchema.nullable(),
  title: z.string(),
  heroHeading: z.string().nullable(),
  heroSubheading: z.string().nullable(),
  heroMediaId: UuidSchema.nullable(),
  heroVideoId: UuidSchema.nullable(),
  tileMediaId: UuidSchema.nullable(),
  aboutHeading: z.string().nullable(),
  aboutHtml: z.string().nullable(),
  metaTitle: z.string().nullable(),
  metaDescription: z.string().nullable(),
  metaKeywords: z.string().nullable(),
  ogImageId: UuidSchema.nullable(),
  status: WebsitePublishStatusSchema,
  publishedAt: IsoDateTimeSchema.nullable(),
  sortOrder: z.number().int(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
  categoryPageIds: z.array(UuidSchema),
  sliders: z.array(sliderOut),
  faqs: z.array(faqOut),
  reels: z.array(reelOut),
  seoLinks: z.array(seoLinkOut),
  reviews: z.array(reviewOut),
});
export type WebsiteDestinationPageDetail = z.infer<typeof WebsiteDestinationPageDetailSchema>;
